import React from 'react';
import type { AreaReport, EmployeeReport } from '../data/types';

interface AreaCardProps {
  report: AreaReport;
  onSelectEmployee?: (employee: EmployeeReport) => void;
}

// ── Colores de celda (cortes editables) ───────────────────────────────
// Verde ≥ 100% · Amarillo 80–99% · Rojo < 80%
const COLOR_VERDE_MIN = 1.0;
const COLOR_AMARILLO_MIN = 0.8;

function cellColor(value: number): string {
  if (value >= COLOR_VERDE_MIN) return 'bg-green-200 text-green-900';
  if (value >= COLOR_AMARILLO_MIN) return 'bg-amber-100 text-amber-900';
  return 'bg-red-200 text-red-900';
}

function formatPct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

const PROCURADORES_CARD = 'Procuradores';

export function AreaCard({ report, onSelectEmployee }: AreaCardProps) {
  const isProcuradores = report.area === PROCURADORES_CARD;

  return (
    <div className="bg-white rounded-xl shadow overflow-hidden border border-slate-200">
      {/* Totales del área: horas cargadas + valor UF facturado (del período) */}
      <div className="grid grid-cols-2 bg-fycom-navy text-white">
        <div className="px-4 py-2 border-r border-white/15">
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-300">
            Horas
          </div>
          <div className="text-lg font-bold leading-tight">
            {report.actualTotalHours.toFixed(1)}
          </div>
        </div>
        <div className="px-4 py-2">
          <div className="text-[10px] font-medium uppercase tracking-wide text-slate-300">
            Valor UF
          </div>
          <div className="text-lg font-bold leading-tight">
            UF {report.valueUF.toFixed(1)}
          </div>
        </div>
      </div>

      {/* Header: título + tabla Cumple/No Cumple (Total · Facturables) */}
      <table className="w-full text-sm border-collapse table-fixed">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-300">
            <th className="text-left px-4 py-2 font-bold text-slate-900">
              {report.area}
            </th>
            <th className="text-center px-4 py-2 font-semibold text-slate-600 w-28">
              Total
            </th>
            <th className="text-center px-4 py-2 font-semibold text-slate-600 w-28">
              Facturables
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-green-100 border-b border-slate-200">
            <td className="px-4 py-1.5 font-medium text-green-900">Cumple</td>
            <td className="px-4 py-1.5 text-center font-semibold text-green-900">
              {formatPct(report.cumpleTotalPct)}
            </td>
            <td className="px-4 py-1.5 text-center font-semibold text-green-900">
              {formatPct(report.cumpleBillablePct)}
            </td>
          </tr>
          <tr className="bg-red-100 border-b border-slate-300">
            <td className="px-4 py-1.5 font-medium text-red-900">No Cumple</td>
            <td className="px-4 py-1.5 text-center font-semibold text-red-900">
              {formatPct(1 - report.cumpleTotalPct)}
            </td>
            <td className="px-4 py-1.5 text-center font-semibold text-red-900">
              {formatPct(1 - report.cumpleBillablePct)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Tabla de colaboradores */}
      <table className="w-full text-sm border-collapse table-fixed">
        <thead className="bg-slate-50 text-slate-600">
          <tr className="border-b border-slate-200">
            <th className="text-left px-4 py-2 font-semibold">Usuario</th>
            <th className="text-center px-4 py-2 font-semibold w-28">
              Cumplimiento Total
            </th>
            <th className="text-center px-4 py-2 font-semibold w-28">
              Cumplimiento Facturado
            </th>
          </tr>
        </thead>
        <tbody>
          {isProcuradores
            ? renderProcuradores(report.employees, onSelectEmployee)
            : report.employees.map((e) => (
                <EmployeeRow key={e.employeeId} e={e} onSelect={onSelectEmployee} />
              ))}
        </tbody>
      </table>
    </div>
  );
}

function EmployeeRow({
  e,
  indent,
  onSelect,
}: {
  e: EmployeeReport;
  indent?: boolean;
  onSelect?: (employee: EmployeeReport) => void;
}) {
  return (
    <tr
      className={`border-b border-slate-100 ${onSelect ? 'cursor-pointer hover:bg-slate-50' : ''}`}
      onClick={onSelect ? () => onSelect(e) : undefined}
    >
      <td
        className={`px-4 py-1.5 text-slate-900 ${indent ? 'pl-8' : ''}`}
      >
        {e.displayName}
      </td>
      <td className={`px-4 py-1.5 text-center font-medium ${cellColor(e.pctTotal)}`}>
        {formatPct(e.pctTotal)}
      </td>
      <td className={`px-4 py-1.5 text-center font-medium ${cellColor(e.pctBillable)}`}>
        {formatPct(e.pctBillable)}
      </td>
    </tr>
  );
}

// Procuradores: agrupados por su sub-área real (Corporativo / Tributarios),
// con un encabezado de sub-área antes de cada grupo.
function renderProcuradores(
  employees: EmployeeReport[],
  onSelect?: (employee: EmployeeReport) => void
) {
  const groups = new Map<string, EmployeeReport[]>();
  for (const e of employees) {
    const key = e.subArea || 'Sin área';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(e);
  }

  const rows: React.ReactNode[] = [];
  for (const [subArea, members] of groups) {
    rows.push(
      <tr key={`h-${subArea}`} className="bg-slate-50 border-b border-slate-200">
        <td colSpan={3} className="px-4 py-1 font-semibold text-slate-700">
          {subArea}
        </td>
      </tr>
    );
    for (const e of members) {
      rows.push(<EmployeeRow key={e.employeeId} e={e} indent onSelect={onSelect} />);
    }
  }
  return rows;
}
